import { initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { logError } from './utils/error-handler';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

let combinedCache = null;
let combinedPromise = null;
let currentLanguage = 'en';

function initLanguage() {
  if (typeof window === 'undefined') return;

  const savedLang = localStorage.getItem('portfolio_language');
  const browserLang = navigator.language.startsWith('ar') ? 'ar' : 'en';
  currentLanguage = savedLang || browserLang;
  updateHTMLAttributes(currentLanguage);
}

initLanguage();
refreshData();

function updateHTMLAttributes(lang) {
  if (typeof window === 'undefined') return;

  const html = document.documentElement;
  html.setAttribute('lang', lang);
  html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
}

export function setLanguage(lang) {
  if (lang === 'en' || lang === 'ar') {
    currentLanguage = lang;
    if (typeof window !== 'undefined') {
      localStorage.setItem('portfolio_language', lang);
      updateHTMLAttributes(lang);
      window.dispatchEvent(
        new CustomEvent('languageChanged', { detail: { language: lang } })
      );
    }
  }
}

export function getLanguage() {
  return currentLanguage;
}

function getDataInLanguage(data) {
  if (!data) return null;
  return data[currentLanguage] || data['en'];
}

async function fetchCombinedOnce() {
  combinedCache = null;
  combinedPromise = (async () => {
    try {
      const localData = await loadLocalJSON();
      if (localData) {
        combinedCache = localData;
        return localData;
      }

      const docSnap = await getDoc(
        doc(firestore, 'Portfolio-Website', 'combined')
      );
      if (!docSnap.exists()) {
        logError(
          new Error('Combined document not found in Firestore'),
          'fetchCombinedOnce',
          {}
        );
        return null;
      }
      combinedCache = docSnap.data();
      return combinedCache;
    } catch (error) {
      logError(error, 'fetchCombinedOnce', {});
      throw error;
    } finally {
      combinedPromise = null;
    }
  })();

  return combinedPromise;
}

export async function preloadPortfolioData() {
  try {
    await fetchCombinedOnce();
  } catch (error) {
    logError(error, 'preloadPortfolioData', {});
  }
}

async function loadLocalJSON() {
  try {
    const response = await fetch(
      `${window.location.protocol}//${window.location.host}/data/combined.json`
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logError(error, 'loadLocalJSON', {});
    return null;
  }
}

// Helper to extract bio data with consistent transformation
function extractBio(bioData) {
  if (!bioData) return null;
  const {
    name = '',
    roles = [],
    description = '',
    github = '',
    resume = '',
    linkedin = '',
    HeroImg: heroImgURL,
  } = bioData;
  return { name, roles, description, github, resume, linkedin, heroImgURL };
}

// Helper to extract skills data with consistent transformation
function extractSkills(skillsData) {
  if (!skillsData) return null;
  return Object.entries(skillsData).map(([category, skills]) => ({
    category,
    skills,
  }));
}

// Helper to extract education data with consistent transformation
function extractEducation(educationData) {
  if (!educationData) return [];
  return Object.keys(educationData).map((key) => ({
    id: educationData[key].id || '',
    ...educationData[key],
  }));
}

// Helper to extract experiences data with consistent transformation
function extractExperiences(experiencesData) {
  if (!experiencesData) return [];
  return Object.keys(experiencesData).map((key) => ({
    id: experiencesData[key].id || '',
    ...experiencesData[key],
  }));
}

// Helper to extract projects data with consistent transformation
function extractProjects(projectsData) {
  if (!projectsData) return [];
  return Object.values(projectsData).map((p) => ({
    ...p,
    member: Object.values(p.member || {}),
  }));
}

// Helper to extract testimonials data
function extractTestimonials(testimonialsData) {
  return testimonialsData || [];
}

// Helper to extract translations data
function extractTranslations(translationsData) {
  return translationsData || {};
}

// Synchronous getters (use after preloadPortfolioData())
export function getBio() {
  return extractBio(getDataInLanguage(combinedCache?.bio));
}

export function getSkills() {
  return extractSkills(getDataInLanguage(combinedCache?.skills));
}

export function getEducation() {
  return extractEducation(getDataInLanguage(combinedCache?.education));
}

export function getExperiences() {
  return extractExperiences(getDataInLanguage(combinedCache?.experiences));
}

export function getProjects() {
  return extractProjects(getDataInLanguage(combinedCache?.projects));
}

export function getTestimonials() {
  return extractTestimonials(getDataInLanguage(combinedCache?.testimonials));
}

export function getTranslations() {
  return extractTranslations(getDataInLanguage(combinedCache?.translations));
}

export function getRawData(section) {
  return combinedCache?.[section];
}

export function isDataLoaded() {
  return !!combinedCache;
}

export function refreshData() {
  combinedCache = null;
}

export { firestore, app };
